import React from 'react'
import {
  IconBitbucket,
  IconDownload,
  IconGithub
} from '@contrast-security-inc/design-system-foundations'
import {
  stepAgentConfig,
  stepSelectContainerMethod,
  stepInteractContrastUI,
  stepAgentConfigPipeline,
  stepSelectOperatingSystemNormalize
} from './common'
import { ImageIcon } from '../common/image-icon'
import {
  stepSelectKubernetesOperator,
  stepKubernetesDownloadSetup,
  stepKubernetesLabelDeployments as stepKubernetesLabelDeploymentsJava,
  stepKubernetesInstallDeployHelm
} from './common-kubernetes'
import {
  stepContainerInstallOperator,
  stepContainerConfigureOperator,
  stepContainerInjectWorkloads as stepContainerInjectWorkloadsJava,
  stepContainerClusterAgentAndAgent
} from './common-container'
import {
  logo_axis2,
  logo_debian,
  logo_docker,
  logo_glassfish,
  logo_java,
  logo_jboss,
  logo_jetty,
  logo_manual,
  logo_pipeline,
  logo_rpm,
  logo_tomcat,
  logo_weblogic,
  logo_websphere,
  logo_wildfly
} from '../images'
import { formatStyles } from '../../../../react-intl-helpers'
import { OPERATING_SYSTEM } from '../common/constants'

const stepKubernetesLabelDeployments =
  stepKubernetesLabelDeploymentsJava('JAVA')

const stepContainerInjectWorkloads = stepContainerInjectWorkloadsJava('JAVA')

const commonFinalSteps = [stepInteractContrastUI]

const stepInstallAgent = {
  titleMsg: 'title_installAgent',
  Content: { message: 'detail_downloadJARFile' }
}

const stepInstallContrastAgent = {
  titleMsg: 'title_installContrastJavaAgent',
  Content: { message: 'detail_installContrastJavaAgent' }
}

// TODO more constants for code
const jbossWildflyCode = `<server-group ...>
  <jvm name="default">
    <jvm-options>
      <option value="-javaagent:<YourContrastJarPath>"/>
    </jvm-options> </jvm> ...
</server-group>`

const applicationDeploymentMethod = {
  titleMsg: 'title_applicationDeploymentMethod',
  defaultIsOpen: true,
  Content: {
    options: [
      {
        value: 'local',
        Content: {
          message: 'title_installManually'
        },
        icon: <ImageIcon src={logo_manual} alt="manually" />
      },
      {
        value: 'container',
        Content: {
          message: 'title_installContainer'
        },
        icon: <ImageIcon src={logo_docker} alt="container" />
      },
      {
        value: 'pipeline',
        Content: {
          message: 'title_installPipeline'
        },
        icon: <ImageIcon src={logo_pipeline} alt="container" />
      }
    ]
  }
}

const agentInstallationMethods = {
  titleMsg: 'title_agentInstallationMethod',
  defaultIsOpen: true,
  Content: {
    options: [
      {
        value: 'direct',
        Content: { message: 'title_directDownload' },
        icon: <IconDownload size="md" />
      },
      {
        value: 'debian',
        Content: 'Debian',
        icon: <ImageIcon alt="Debian" src={logo_debian} />
      },
      {
        value: 'rpm',
        Content: 'RPM',
        icon: <ImageIcon alt="RPM" src={logo_rpm} />
      }
    ]
  }
}

const agentInstallationMethodsWindows = {
  titleMsg: 'title_agentInstallationMethod',
  defaultIsOpen: true,
  Content: {
    options: [
      {
        defaultIsOpen: true,
        value: 'direct',
        Content: { message: 'title_directDownload' },
        icon: <IconDownload size="md" />
      }
    ]
  }
}

const stepAppServer = {
  titleMsg: 'title_selectAppServer',
  branchKey: 'java-app-server',
  Content: {
    options: [
      {
        value: 'tomcat',
        Content: 'Tomcat',
        icon: <ImageIcon src={logo_tomcat} alt="Tomcat" />
      },
      {
        value: 'jboss',
        Content: 'JBoss',
        icon: <ImageIcon src={logo_jboss} alt="JBoss" />
      },
      {
        value: 'wildfly',
        Content: 'WildFly',
        icon: <ImageIcon src={logo_wildfly} alt="Wildfly" />
      },
      {
        value: 'websphere',
        Content: 'WebSphere',
        icon: <ImageIcon src={logo_websphere} alt="Websphere" />
      },
      {
        value: 'weblogic',
        Content: 'WebLogic',
        icon: <ImageIcon src={logo_weblogic} alt="Weblogic" />
      },
      {
        value: 'jetty',
        Content: 'Jetty',
        icon: <ImageIcon src={logo_jetty} alt="Jetty" />
      },
      {
        value: 'axis2',
        Content: 'Axis2',
        icon: <ImageIcon src={logo_axis2} alt="Axis2" />
      },
      {
        value: 'glassfish',
        Content: 'GlassFish',
        icon: <ImageIcon src={logo_glassfish} alt="Glassfish" />
      },
      {
        value: 'jar',
        Content: 'Executable Jar',
        icon: <ImageIcon src={logo_java} alt="Java" />
      }
    ]
  },
  branches: {
    jar: [
      {
        defaultIsOpen: true,
        titleMsg: 'title_specifyPath',
        Content: {
          message: 'detail_appConfigJar',
          values: {
            path: '<ApplicationJarPath>'
          }
        }
      },
      ...commonFinalSteps
    ],
    tomcat: [
      {
        defaultIsOpen: true,
        titleMsg: 'title_configureApp',
        Content: { message: 'detail_appConfigTomcat' }
      },
      ...commonFinalSteps
    ],
    jboss: [
      {
        titleMsg: 'title_configureApp',
        defaultIsOpen: true,
        Content: {
          message: 'detail_appConfigJBOSSWildfly',
          values: {
            appserver: 'JBoss',
            appserverCapital: 'JBOSS',
            code: jbossWildflyCode
          }
        }
      },
      ...commonFinalSteps
    ],
    wildfly: [
      {
        titleMsg: 'title_configureApp',
        defaultIsOpen: true,
        Content: {
          message: 'detail_appConfigJBOSSWildfly',
          values: {
            appserver: 'Wildfly',
            appserverCapital: 'WILDFLY',
            code: jbossWildflyCode
          }
        }
      },
      ...commonFinalSteps
    ],
    websphere: [
      {
        defaultIsOpen: true,
        titleMsg: 'title_configureApp',
        Content: {
          message: 'detail_appConfigWebsphere',
          values: {
            ...formatStyles.doclink({
              path: 'websphere.html#use-java-2-with-websphere'
            }),
            code: `<WebsphereDirectory>\\AppServer\\profiles\\AppSrv01\\config\\cells\\<CellName>\\nodes\\<NodeName>\\servers\\server1\\server.xml

  <jvmEntries genericJvmArguments="-javaagent:<YourContrastJarPath> -Xshareclasses:none">
  ...
  </jvmEntries>

  `
          }
        }
      },
      ...commonFinalSteps
    ],
    weblogic: [
      {
        defaultIsOpen: true,
        titleMsg: 'title_configureApp',
        Content: { message: 'detail_appConfigWeblogic' }
      },
      ...commonFinalSteps
    ],
    jetty: [
      {
        defaultIsOpen: true,
        titleMsg: 'title_configureApp',
        Content: {
          message: 'detail_javaAppConfigGenericAppServer',
          values: { appserver: 'Jetty' }
        }
      },
      ...commonFinalSteps
    ],
    axis2: [
      {
        defaultIsOpen: true,
        titleMsg: 'title_configureApp',
        Content: {
          message: 'detail_javaAppConfigGenericAppServer',
          values: { appserver: 'Axis2' }
        }
      },
      ...commonFinalSteps
    ],
    glassfish: [
      {
        defaultIsOpen: true,
        titleMsg: 'title_configureApp',
        Content: {
          message: 'detail_javaAppConfigGenericAppServer',
          values: { appserver: 'Glassfish' }
        }
      },
      ...commonFinalSteps
    ]
  }
}

const directSteps = [
  { defaultIsOpen: true, ...stepInstallAgent },
  stepAgentConfig,
  stepAppServer
]

const agentInstallationMethodBranches = {
  direct: directSteps,
  debian: [
    {
      defaultIsOpen: true,
      titleMsg: 'title_getRepoPackages',
      Content: { message: 'detail_getRepoPackages' }
    },
    stepInstallContrastAgent,
    stepAgentConfig,
    stepAppServer
  ],
  rpm: [
    {
      defaultIsOpen: true,
      titleMsg: 'title_getRepoPackages',
      Content: {
        message: 'detail_getRepoPackagesRPM',
        values: {
          code: `OSREL=$(rpm -E "%{rhel}")
sudo -E tee /etc/yum.repos.d/contrast.repo << EOF
[contrast]
name=contrast repo
baseurl=https://pkg.contrastsecurity.com/rpm-public/centos-$OSREL/
gpgcheck=0
enabled=1 EOF`
        }
      }
    },
    stepInstallContrastAgent,
    stepAgentConfig,
    stepAppServer
  ]
}

const kubernetesSteps = [
  {
    ...stepSelectKubernetesOperator,
    branchKey: 'java-kubernetes-operator',
    branches: {
      helm: [
        stepKubernetesDownloadSetup,
        stepKubernetesLabelDeployments,
        stepKubernetesInstallDeployHelm,
        ...commonFinalSteps
      ],
      manifest: [
        stepContainerInstallOperator,
        stepContainerConfigureOperator,
        stepContainerInjectWorkloads,
        stepContainerClusterAgentAndAgent,
        ...commonFinalSteps
      ]
    }
  }
]

const dockerStep = {
  defaultIsOpen: true,
  titleMsg: 'title_buildImageWithAgent',
  Content: {
    message: 'detail_dockerBuildImageAgentJava',
    values: {
      code: `COPY --from=contrast/agent-java:latest /contrast/contrast-agent.jar /contrast.jar
ENV JAVA_TOOL_OPTIONS="-javaagent:/contrast.jar"
`
    }
  }
}

const linuxContainerStepsBranches = {
  docker: [dockerStep, stepAgentConfig, ...commonFinalSteps],
  kubernetes: kubernetesSteps
}

const linuxContainerSteps = [
  {
    ...stepSelectContainerMethod,
    branchKey: 'java-container-method',
    branches: linuxContainerStepsBranches
  }
]

const stepSelectPipelineMethod = {
  defaultIsOpen: true,
  titleMsg: 'title_selectDeployMethodPipeline',
  Content: {
    options: [
      {
        value: 'github',
        Content: 'Github',
        icon: <IconGithub color="black" />
      },
      {
        value: 'bitbucket',
        Content: 'Bitbucket',
        icon: <IconBitbucket color="blue50"></IconBitbucket>
      }
    ]
  }
}

const githubInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installGithubJava',
    values: {
      code1: `- name: Install Agent
  shell: bash
  run: curl -L https://download.java.contrastsecurity.com/latest -o contrast.jar
`,
      code2: `env: 
  CONTRAST__API__TOKEN: \${{ secrets.ContrastAPIToken }} 
  CONTRAST__APPLICATION__NAME: \${{ secrets.ContrastApplicationName }} 
  CONTRAST__APPLICATION__METADATA: \${{ secrets.ContrastApplicationMetadata }} 
  JAVA_TOOL_OPTIONS: "-javaagent:/contrast.jar"
`,
      token: '{{ secrets.ContrastAPIToken }}',
      name: '{{ secrets.ContrastApplicationName }}',
      metadata: '{{ secrets.ContrastApplicationMetadata }}',
      options: '"-javaagent:/contrast.jar"'
    }
  }
}

const bitbucketInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installBitbucketJava',
    values: {
      code: `- step:
  script:
    - curl -L https://download.java.contrastsecurity.com/latest -o contrast.jar
`
    }
  }
}

const pipelineSteps = [
  {
    ...stepSelectPipelineMethod,
    branchKey: 'linux-java-pipeline-method',
    branches: {
      github: [
        githubInstallAgent,
        stepAgentConfigPipeline,
        ...commonFinalSteps
      ],
      bitbucket: [
        bitbucketInstallAgent,
        stepAgentConfigPipeline,
        ...commonFinalSteps
      ]
    }
  }
]

const linuxSelectAgentInstallationMethod = {
  local: [
    {
      ...agentInstallationMethods,
      branchKey: 'java-agent-installation-methods',
      branches: agentInstallationMethodBranches
    }
  ],
  container: linuxContainerSteps,
  pipeline: pipelineSteps
}

const windowsStepSelectContainerMethod = {
  defaultIsOpen: true,
  titleMsg: 'title_selectDeployMethod',
  Content: {
    options: [
      {
        value: 'docker',
        Content: 'Dockerfile',
        icon: <ImageIcon src={logo_docker} alt="Dockerfile" />
      }
    ]
  }
}

const windowsContainerSteps = [
  {
    ...windowsStepSelectContainerMethod,
    branchKey: 'java-container-method'
  },
  dockerStep,
  stepAgentConfig,
  ...commonFinalSteps
]

const windowsSelectAgentInstallationMethod = {
  local: [
    {
      ...agentInstallationMethodsWindows,
      branchKey: 'java-agent-installation-methods'
    },
    ...directSteps
  ],
  container: windowsContainerSteps,
  pipeline: pipelineSteps
}

export const javaWizardStepsNormalizeAgentDownload = [
  {
    ...stepSelectOperatingSystemNormalize,
    branchKey: OPERATING_SYSTEM.KEY,
    branches: {
      linux: [
        {
          ...applicationDeploymentMethod,
          branchKey: 'java-deployment-method-linux',
          branches: linuxSelectAgentInstallationMethod
        }
      ],
      windows: [
        {
          ...applicationDeploymentMethod,
          branchKey: 'java-deployment-method-windows',
          branches: windowsSelectAgentInstallationMethod
        }
      ],
      macos: [
        {
          ...applicationDeploymentMethod,
          branchKey: 'java-deployment-method-linux',
          branches: linuxSelectAgentInstallationMethod
        }
      ]
    }
  }
]
